import React, { useEffect, useRef, useState } from 'react';
import './Lending.css';
import thumbnail from '../../assets/thumbnail.png';
import Hls from 'hls.js';
import { handleAuthButtonClick, oauth } from '../../api/login';
import Layout from '../../components/Layout';
import Logo from '../../components/Logo';
import { accessToken } from '../../constants/constants';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../../components/Modals/LoginModal';
import useModalManagement from '../../hooks/useModalManagement';
import { openModal } from '../../redux/modals';
import { useDispatch, useSelector } from 'react-redux';
import useUserData from '../../hooks/useUserData';
import { setToken } from '../../redux/user';
import { ArrowLeftEndOnRectangleIcon } from '@heroicons/react/24/solid';

function Lending() {
  const videoPlayerRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.token);
  const { modals, handleCloseModal } = useModalManagement();
  const [showSeoText, setShowSeoText] = useState(false);

  const faqItems = [
    {
      answer: `нажмите "Добавить устройство" на главной странице. Выберите платформу, FQBN (Fully Qualified Board Name - полное квалифицированное имя платы) и придумайте название для устройства`,
      title: 'Как добавить новое устройство?',
    },
    {
      answer: `чтобы обновить прошивку, вы можете самостоятельно изменить базовую прошивку или воспользоваться ИИ, скомпилировать её в облаке и установить на устройство по OTA`,
      title: 'Как обновить прошивку устройства?',
    },
    {
      answer: `гибкий веб-интерфейс подстраивается под вашу прошивку, позволяя управлять устройством. Также можно управлять через приложение Яндекса`,
      title: 'Как взаимодействовать с устройством?',
    },
  ];

  const openLogin = () => dispatch(openModal('loginModal'));
  const updateToken = (t) => dispatch(setToken(t));

  useEffect(() => {
    const videoPlayer = videoPlayerRef.current;
    videoPlayer.volume = 0.0;
    if (videoPlayer) {
      const url = 'https://iopp.io/demo-video/demo.m3u8';
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(videoPlayer);
      } else if (videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
        videoPlayer.src = url;
      }
    }
  }, []);

  return (
    <Layout header="false">
      <div
        className="min-h-vh flex min-h-[10dvh] flex-col items-center justify-center text-center pt-[50px]"
        id="logo-container"
      >
        <div id="logo-landing" className="rounded">
          <Logo />
        </div>
      </div>

      <div className="flex flex-col items-center justify-center py-[30px] gap-8">
        {/* Log in Button */}
        <div className="px-4 w-full sm:w-fit">
          <button
            onClick={() => handleAuthButtonClick(openLogin, updateToken)}
            id="login-button"
            className="login-button w-full sm:w-auto flex items-center justify-center gap-3 px-4 py-3 border border-black bg-white text-[2em]"
          >
            <span>авторизация</span>
            <ArrowLeftEndOnRectangleIcon className="w-9 h-9 pt-1" />
          </button>
        </div>

        {/* Video Player */}
        <div className="w-full flex justify-center pt-[100px]">
          <video
            ref={videoPlayerRef}
            id="videoPlayer"
            controls
            poster={thumbnail}
            width={1920}
            height={1080}
            className="w-full max-w-[100%] lg:max-w-[75%] h-auto bg-[#000F000A] shadow-xl"
          />
        </div>
      </div>
      <div className="p-8 pt-0">
        <h1 id="about" className="seo-main-title">
          iopp.io - облачная платформа для управления ESP устройствами
        </h1>
        <h2 className="section-subtitle">
          Универсальный облачный сервис для DIY проектов
        </h2>
        <p>
          универсальный облачный сервис, который позволяет легко подключать,
          управлять и мониторить устройства на базе ESP (ESP8266, ESP32) в
          рамках ваших DIY проектов. Сервис предлагает широкий спектр функций,
          делая управление умным домом или другими проектами интуитивно понятным
          и удобным
        </p>

        <div className="features mt-5">
          <div className="feature">
            <h3 className="pb-3">простое подключение</h3>
            <ul>
              <li>
                <span className="text-sky-600 font-medium">
                  Компиляция прошивок в облаке
                </span>
              </li>
              <li>Быстрое подключение ESP устройств через веб-интерфейс</li>
              <li>Прошивка устройств через USB или по воздуху (OTA)</li>
            </ul>
          </div>

          <div className="feature">
            <h3 className="pb-3">управление и мониторинг</h3>
            <ul>
              <li>Управление устройствами через веб-интерфейс</li>
              <li>Мониторинг состояния в реальном времени</li>
              <li>Подробная история состояний и событий</li>
            </ul>
          </div>

          <div className="feature">
            <h3 className="pb-3">аналитика и статистика</h3>
            <ul>
              <li>Графики показателей в реальном времени</li>
              <li>История всех состояний устройств</li>
              <li>Журнал событий с фильтрацией</li>
            </ul>
          </div>

          <div className="feature">
            <h3 className="pb-3">интеграции</h3>
            <ul>
              <li>
                Поддержка{' '}
                <span className="yandex-gradient font-medium text-[1.1rem]">
                  Яндекс Алисы
                </span>{' '}
                для управления устройствами
              </li>
              <li>Подключение к мобильному приложению Умный дом</li>
              <li>REST API для внешних интеграций</li>
            </ul>
          </div>

          <div className="feature">
            <h3 className="pb-3">триггеры и оповещения</h3>
            <ul>
              <li>Настройка условий для уведомлений</li>
              <li>Мгновенные оповещения в Telegram</li>
              <li>Гибкая настройка условий срабатывания</li>
            </ul>
          </div>

          <div className="feature">
            <h3 className="pb-3">разработка и отладка</h3>
            <ul>
              <li>Встроенный редактор кода с подсветкой синтаксиса</li>

              <li>
                <span className="font-medium text-indigo-800">
                  AI-помощник для генерации кода
                </span>
              </li>

              <li>Компиляция прошивок в облаке</li>
              <li>Отладка через журнал событий</li>
            </ul>
          </div>
        </div>
        <hr />

        <h2 id="faq" className="section-subtitle">
          Часто задаваемые вопросы
        </h2>
        {faqItems.map((faq, index) => (
          <div key={index}>
            <div className="mt-5 pb-5">{faq.title}</div>
            <div className="border bg-slate-100 p-2">{faq.answer}</div>
          </div>
        ))}
      </div>

      {/* SEO текст скрытый для пользователей */}
      <div className="seo-section">
        <button
          className="seo-toggle"
          onClick={() => setShowSeoText(!showSeoText)}
        >
          {showSeoText ? '▲ Свернуть' : '▼ О платформе'}
        </button>
        {showSeoText && (
          <div className="seo-content">
            <h1>
              iopp.io - Управление ESP8266 и ESP32 через облачную платформу
            </h1>

            <h2>Облачная платформа для IoT устройств и умного дома</h2>
            <p>
              <strong>iopp.io</strong> - это современный облачный сервис для
              подключения, управления и мониторинга устройств на базе
              микроконтроллеров <strong>ESP8266 и ESP32</strong>. Платформа
              предоставляет полный цикл разработки и эксплуатации IoT проектов -
              от прошивки устройств до анализа данных и голосового управления.
            </p>

            <h3>Ключевые возможности платформы:</h3>
            <ul>
              <li>
                <strong>Облачная компиляция прошивок</strong> для ESP32 и
                ESP8266 с последующей установкой через OTA (Over-The-Air)
              </li>
              <li>
                <strong>Интеграция с Яндекс Алисой</strong> для голосового
                управления устройствами умного дома
              </li>
              <li>
                <strong>AI-ассистент</strong> для автоматической генерации и
                оптимизации кода
              </li>
              <li>
                <strong>Веб-интерфейс управления</strong> с адаптацией под вашу
                прошивку
              </li>
              <li>
                <strong>Мониторинг в реальном времени</strong> с графиками и
                статистикой
              </li>
              <li>
                <strong>Telegram уведомления</strong> и система триггеров
              </li>
              <li>
                <strong>REST API</strong> для интеграции с внешними системами
              </li>
            </ul>

            <h2>Преимущества для разработчиков IoT</h2>
            <p>
              Платформа <strong>iopp.io</strong> значительно ускоряет разработку
              проектов умного дома на базе ESP микроконтроллеров. Благодаря
              облачной компиляции прошивок и OTA обновлениям вы можете
              развернуть систему управления без глубоких знаний в
              программировании.
            </p>

            <h3>Для каких проектов подходит iopp.io:</h3>
            <ul>
              <li>Умный дом на ESP8266/ESP32 с голосовым управлением</li>
              <li>Системы мониторинга параметров окружающей среды</li>
              <li>DIY проекты домашней автоматизации</li>
              <li>Промышленный IoT для малого и среднего бизнеса</li>
              <li>
                Образовательные проекты по программированию микроконтроллеров
              </li>
            </ul>

            <h2>Как начать работу с платформой</h2>
            <p>
              Чтобы подключить свое первое ESP устройство к{' '}
              <strong>iopp.io</strong>, нажмите кнопку &quot;запустить&quot;,
              зарегистрируйтесь и следуйте инструкциям мастера добавления
              устройств. Платформа поддерживает все популярные модели ESP8266 и
              ESP32, а также предоставляет подробную документацию.
            </p>

            <h3>Процесс подключения устройства:</h3>
            <ol>
              <li>Выберите тип микроконтроллера (ESP32 или ESP8266)</li>
              <li>Настройте FQBN (Fully Qualified Board Name) платы</li>
              <li>Скомпилируйте базовую прошивку в облаке</li>
              <li>Загрузите прошивку через USB или по воздуху (OTA)</li>
              <li>Настройте веб-интерфейс управления под свои нужды</li>
            </ol>

            <p>
              <strong>iopp.io</strong> - это комплексное решение для создания
              систем умного дома и IoT проектов любой сложности. Платформа
              объединяет в себе простоту использования для новичков и мощные
              инструменты для опытных разработчиков.
            </p>

            <h2>Технические особенности</h2>
            <p>
              Платформа использует современные технологии веб-разработки и
              поддерживает все актуальные стандарты связи для IoT устройств.
              Совместимость с популярными датчиками и сенсорами для ESP
              микроконтроллеров делает <strong>iopp.io</strong> универсальным
              решением для большинства проектов автоматизации.
            </p>
          </div>
        )}
      </div>

      {modals.loginModal && (
        <LoginModal
          closeModal={() => handleCloseModal('loginModal')}
          token={token}
        />
      )}
    </Layout>
  );
}

export default Lending;
